// MARK://dhk8/pos/lib/clients/tips
// Typed client for dhk8-pos-tips — reconciled against services/dhk8-pos-tips/src/contract.ts.
//
// A tip is an IMMUTABLE record. `pooled=true` → it goes into the pool to be split among
// servers; `pooled=false` → it's a direct tip kept by `serverId`. The summary endpoint
// aggregates per server (total/pooled/direct) plus pool totals — the input for the
// pool-split calculation shown in the cash-cut / reports view.
//
// Routes: `${origin}/v1/tips/...`. Envelope: { ok, data } | bare T.

import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { PREVIEW_STUB } from '../seed-preview.js'
import { gqlClient } from '../gql.js'
import { PosTipsQuery, PosTipQuery, PosTipSummaryQuery, PosCreateTipMutation } from '../queries/tips.queries.js'

// 2026-05-26 (Lane #178) — antes `${apiOrigin()}/v1/tips` → 404 nginx.
// dhk8-pos-tips expone `/v1/tips/*` y se accede vía `/v1/dhk8-pos-tips/v1/tips/*`.
const BASE = svcUrl('dhk8-pos-tips', '/v1/tips')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── DTOs (mirror of services/dhk8-pos-tips/src/contract.ts) ─────────────────────

export interface TipDto {
  id: string
  tenantId: string
  branchId: string
  orderId: string | null
  paymentId: string | null
  serverId: string
  amountCents: number
  pooled: boolean
  createdAt: string
}

export interface TipSummaryRow {
  serverId: string
  totalCents: number
  pooledCents: number
  directCents: number
}

export interface TipSummaryDto {
  byServer: TipSummaryRow[]
  pooledTotalCents: number
  directTotalCents: number
  totalCents: number
}

export interface CreateTipInput {
  tenantId: string
  branchId: string
  serverId: string
  amountCents: number
  orderId?: string
  paymentId?: string
  pooled?: boolean
}

export interface ListTipsQuery {
  tenantId: string
  branchId?: string
  serverId?: string
  orderId?: string
  pooled?: boolean
  from?: string
  to?: string
  page?: number
  limit?: number
}

export interface TipListResponse {
  items: TipDto[]
  total: number
  page: number
  limit: number
}

export interface TipSummaryQuery {
  tenantId: string
  branchId?: string
  from?: string
  to?: string
}

// ── operations ──────────────────────────────────────────────────────────────────

export async function listTips(query: ListTipsQuery, signal?: AbortSignal): Promise<TipListResponse> {
  // F4.2 / ADR-0009: lectura por el grafo (pos-tips, tenant del JWT). Escrituras REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; serverId?: string; orderId?: string; pooled?: boolean; from?: string; to?: string; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.serverId) vars.serverId = query.serverId
  if (query.orderId) vars.orderId = query.orderId
  if (query.pooled !== undefined) vars.pooled = query.pooled
  if (query.from) vars.from = query.from
  if (query.to) vars.to = query.to
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosTipsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pageRes = res.data?.tips
  return {
    items: (pageRes?.items ?? []) as unknown as TipDto[],
    total: pageRes?.total ?? 0,
    page: pageRes?.page ?? (query.page ?? 1),
    limit: pageRes?.limit ?? (query.limit ?? 20),
  }
}

export async function getTip(id: string, signal?: AbortSignal): Promise<TipDto> {
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosTipQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const t = res.data?.tip
  if (!t) throw new Error(`NOT_FOUND: tip ${id}`)
  return t as unknown as TipDto
}

export async function tipSummary(query: TipSummaryQuery, signal?: AbortSignal): Promise<TipSummaryDto> {
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; from?: string; to?: string } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.from) vars.from = query.from
  if (query.to) vars.to = query.to
  const res = await gqlClient.query(PosTipSummaryQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.tipSummary ?? { byServer: [], pooledTotalCents: 0, directTotalCents: 0, totalCents: 0 }) as unknown as TipSummaryDto
}

export async function createTip(input: CreateTipInput): Promise<TipDto> {
  if (PREVIEW_STUB) {
    return {
      id: `tip-stub-${Date.now().toString(36)}`, tenantId: input.tenantId, branchId: input.branchId,
      orderId: input.orderId ?? null, paymentId: input.paymentId ?? null, serverId: input.serverId,
      amountCents: input.amountCents, pooled: input.pooled ?? false, createdAt: new Date().toISOString(),
    }
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (createTip mutation, tenant del JWT).
  const res = await gqlClient.mutation(PosCreateTipMutation, { input: { branchId: input.branchId, serverId: input.serverId, amountCents: input.amountCents, ...(input.orderId ? { orderId: input.orderId } : {}), ...(input.paymentId ? { paymentId: input.paymentId } : {}), ...(input.pooled !== undefined ? { pooled: input.pooled } : {}) } }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.createTip
  if (!t) throw new Error('CREATE_TIP_FAILED')
  return t as unknown as TipDto
}

/** Convenience: record a tip for the current tenant. Returns the row, or null on failure
 * (tip recording is best-effort — the payment already went through). */
export async function recordTip(
  input: Omit<CreateTipInput, 'tenantId'> & { tenantId?: string },
): Promise<TipDto | null> {
  const tenantId = input.tenantId ?? getTenantId() ?? ''
  if (!tenantId || input.amountCents <= 0) return null
  return createTip({ ...input, tenantId }).catch(() => null)
}
